import { ItemCategory } from '@/types';

function findCategoryRecursive(categories: ItemCategory[], id: number, path: ItemCategory[]) {
    if (!categories?.length) return false;
    for (let category of categories) {
        if (category.id === id || findCategoryRecursive(category.subCategories ?? [], id, path)) {
            path.push(category);
            return true;
        }
    }

    return false;
}

function collectDescendantCategoryIds(category: ItemCategory, ids: Set<number>): void {
    ids.add(category.id);
    category.subCategories?.forEach((sub) => collectDescendantCategoryIds(sub, ids));
}

export function expandCategoryIdsToIncludeDescendants(selectedIds: number[], allCategories: ItemCategory[]): number[] {
    const result = new Set<number>();
    const findAndCollect = (categories: ItemCategory[]) => {
        for (const category of categories) {
            if (selectedIds.includes(category.id)) {
                collectDescendantCategoryIds(category, result);
            } else {
                findAndCollect(category.subCategories || []);
            }
        }
    };
    findAndCollect(allCategories);
    return Array.from(result);
}

interface FlattenCategoriesResultItem {
    path: ItemCategory[];
    item: ItemCategory;
}
export function flattenCategories(categories: ItemCategory[]) {
    const result: FlattenCategoriesResultItem[] = [];

    function iterate(subCategories: ItemCategory[], parentPath: ItemCategory[]) {
        for (let category of subCategories) {
            const currentPath = [...parentPath, category];
            iterate(category.subCategories, currentPath);
            result.push({
                path: currentPath,
                item: category,
            });
        }
    }

    iterate(categories, []);

    return result;
}

//TODO: add non recursive version?
export function findCategory(categories: ItemCategory[], id: number) {
    const path: ItemCategory[] = [];
    findCategoryRecursive(categories, id, path);
    return { path, node: path[0] };
}

export function findTopLevelCategory(categories: ItemCategory[], id: number) {
    const path: ItemCategory[] = [];
    findCategoryRecursive(categories, id, path);

    if (path.length === 0) {
        return null;
    }

    return path[path.length - 1];
}

export function findCategoryName(categories: ItemCategory[], id: number) {
    const result = findCategory(categories, id);

    if (result && result.node) {
        return result.node.name;
    }

    return null;
}

/**
 * Map every category id (including sub-categories) to its top-level ancestor id.
 * Useful when you need many id→top-level lookups: build the map once instead of
 * calling `findTopLevelCategory` per id.
 */
export function buildTopLevelCategoryMap(categories: ItemCategory[]): Map<number, number> {
    const map = new Map<number, number>();
    function walk(category: ItemCategory, topLevelId: number) {
        map.set(category.id, topLevelId);
        for (const sub of category.subCategories ?? []) {
            walk(sub, topLevelId);
        }
    }
    for (const top of categories) {
        walk(top, top.id);
    }
    return map;
}
